import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { StarFilled } from '@ant-design/icons';
import { Checkbox, Flex, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import StatusDisplayExtended from '@totem/components/ticketing/statusDisplayExtended';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import TagDisplay, {
  TICKET_PRIORITY_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { formatDate, isNotNull, isNull } from '@totem/utilities/common';

import '../../ticketing.css';

const styles = {
  icon: {
    fontSize: '24px',
  },
};

const TicketsFavoritesTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const {
    ticketData,
    loading,
    totalRecords,
    onTicketSelected,
    selectedTickets,
    selectTicket,
    deselectTicket,
  } = useContext(TicketingContext);

  const { getFilterOptions, getFilterValues, handleTableChange, input } =
    useContext(InputContext);

  const callTicketAction = (ticket: TicketQueryResult) => {
    if (typeof onTicketSelected === 'function') {
      onTicketSelected(ticket.ticket.id, ticket);
    }
  };

  const getTicketClickAction = (ticket: TicketQueryResult) => {
    let foundIdx = -1;
    let suffix = <div />;

    if (isNotNull(ticket) && isNotNull(ticket.ticket.favorites)) {
      foundIdx = ticket.ticket.favorites.findIndex(
        (chk) => chk === userProfile.id,
      );
    }

    if (foundIdx >= 0) {
      suffix = (
        <div>
          <StarFilled style={{ color: '#FFC107' }} />
        </div>
      );
    }

    if (typeof onTicketSelected === 'function') {
      return (
        <Flex justify="space-between" align="center">
          <div
            onClick={() => {
              callTicketAction(ticket);
            }}
          >
            {ticket.ticket.number}
          </div>
          {suffix}
        </Flex>
      );
    }

    return (
      <Flex justify="space-between" align="center">
        <div>
          <Link to={`/dashboard/ticket/${ticket.ticket.id}`}>
            {ticket.ticket.number}
          </Link>
        </div>
        {suffix}
      </Flex>
    );
  };

  const getControlSystemAlternate = (checkTicket: TicketQueryResult) => {
    if (
      typeof checkTicket !== 'undefined' &&
      checkTicket !== null &&
      checkTicket.ticket.attributes !== null
    ) {
      for (let idx = 0; idx < checkTicket.ticket.attributes.length; idx++) {
        const attribute = checkTicket.ticket.attributes[idx];
        if (attribute.name === 'ControlSystemType') {
          return attribute.value;
        }
      }
    }
    return '';
  };

  const getControlSystemDisplay = (checkTicket: TicketQueryResult) => {
    if (
      typeof checkTicket !== 'undefined' &&
      checkTicket !== null &&
      checkTicket.controlSystem !== null &&
      checkTicket.controlSystem.id !== null &&
      checkTicket.controlSystem.id !== EMPTY_ID
    ) {
      return <span>{checkTicket.controlSystem.name}</span>;
    } else if (
      checkTicket.controlSystem !== null &&
      checkTicket.controlSystem.name !== ''
    ) {
      return <span>{checkTicket.controlSystem.name}</span>;
    }

    return <span>{getControlSystemAlternate(checkTicket)}</span>;
  };

  const adminColumns: ColumnProps<TicketQueryResult>[] = [
    {
      title: '',
      dataIndex: 'ticket.id',
      key: 'id',
      width: 26,
      render: (_, ticket: TicketQueryResult) => {
        const isChecked =
          selectedTickets.findIndex((chk) => chk.id === ticket.ticket.id) > -1;

        return (
          <IBTooltip content={permissions.ticketingAdministrator.tooltip}>
            <Checkbox
              checked={isChecked}
              onClick={() => {
                if (isChecked) {
                  deselectTicket(ticket.ticket.id);
                } else {
                  selectTicket(ticket.ticket.id, ticket.ticket.number);
                }
              }}
            />
          </IBTooltip>
        );
      },
    },
  ];

  const columns: ColumnProps<TicketQueryResult>[] = [
    ...(permissions.ticketingAdministrator.hasPermissions(userProfile)
      ? adminColumns
      : []),
    {
      title: '',
      dataIndex: 'ticket.type',
      key: 'type',
      width: 26,
      render: (_, ticket: TicketQueryResult) => (
        <Tooltip title={ticket.ticket.type}>
          <IBDIcon name={ticket.ticket.type} style={styles.icon} />
        </Tooltip>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.type === compB.ticket.type
          ? 0
          : compA.ticket.type > compB.ticket.type
            ? 1
            : -1,
      minWidth: 20,
      filterMultiple: true,
      filteredValue: getFilterValues('type'),
      filters: getFilterOptions('type'),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'Number',
      dataIndex: 'ticket.number',
      key: 'number',
      render: (_, ticket: TicketQueryResult) => getTicketClickAction(ticket),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.number === compB.ticket.number
          ? 0
          : compA.ticket.number > compB.ticket.number
            ? 1
            : -1,
    },
    {
      title: 'Category',
      dataIndex: 'ticket.category',
      key: 'category',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.category,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.category === compB.ticket.category
          ? 0
          : compA.ticket.category > compB.ticket.category
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('category'),
      filters: getFilterOptions('category'),
    },
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization',
      render: (_, ticket: TicketQueryResult) => ticket.organization.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.organization.name === compB.organization.name
          ? 0
          : compA.organization.name > compB.organization.name
            ? 1
            : -1,
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, ticket: TicketQueryResult) => ticket.building.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.building.name === compB.building.name
          ? 0
          : compA.building.name > compB.building.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystem',
      render: (_, ticket: TicketQueryResult) => getControlSystemDisplay(ticket),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.controlSystem.name === compB.controlSystem.name
          ? 0
          : compA.controlSystem.name > compB.controlSystem.name
            ? 1
            : -1,
    },
    {
      title: 'Summary',
      dataIndex: 'ticket.summary',
      key: 'summary',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.summary === compB.ticket.summary
          ? 0
          : compA.ticket.summary > compB.ticket.summary
            ? 1
            : -1,
    },
    {
      title: 'Priority',
      dataIndex: 'ticket.priority',
      key: 'priority',
      render: (_, ticket: TicketQueryResult) => (
        <TagDisplay
          value={ticket.ticket.priority}
          config={TICKET_PRIORITY_STYLES}
        />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.priority === compB.ticket.priority
          ? 0
          : compA.ticket.priority > compB.ticket.priority
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('priority'),
      filters: getFilterOptions('priority'),
    },
    {
      title: 'Status',
      dataIndex: 'ticket.status',
      key: 'status',
      render: (_, ticket: TicketQueryResult) => (
        <StatusDisplayExtended ticket={ticket.ticket} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.status === compB.ticket.status
          ? 0
          : compA.ticket.status > compB.ticket.status
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('status'),
      filters: getFilterOptions('status'),
    },
    {
      title: 'Related',
      dataIndex: 'ticket.relatedTickets',
      key: 'summary',
      render: (_, ticket: TicketQueryResult) =>
        isNull(ticket.relatedTickets) ? (
          <span />
        ) : (
          ticket.relatedTickets.map((rel) => (
            <div key={rel.ticket.ticket.id}>
              <span title={rel.relationship}>
                {rel.relationship.toUpperCase().charAt(0)}
              </span>
              :{' '}
              <Link to={`/dashboard/ticket/${rel.ticket.ticket.id}`}>
                {rel.ticket.ticket.number}
              </Link>
            </div>
          ))
        ),
    },
    {
      title: 'Created',
      dataIndex: 'ticket.createdAt',
      key: 'createdAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.createdAt),
      sorter: (compA, compB) =>
        compA.ticket.createdAt === compB.ticket.createdAt
          ? 0
          : compA.ticket.createdAt > compB.ticket.createdAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Updated',
      dataIndex: 'ticket.updatedAt',
      key: 'updatedAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.updatedAt),
      sorter: (compA, compB) =>
        compA.ticket.updatedAt === compB.ticket.updatedAt
          ? 0
          : compA.ticket.updatedAt > compB.ticket.updatedAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
  ];

  const getTickets = () => {
    if (ticketData !== null) {
      if (ticketData.tickets !== null) {
        return ticketData.tickets;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getTickets()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.ticket.id}
    />
  );
};

export default TicketsFavoritesTable;
